/**
 * The $25 joining bonus on the onboarding hub.
 *
 * Three states, all positive: a countdown while she can still win it, "you
 * finished in time" while she waits for activation, and "earned" once it is
 * hers. The API sends `bonusOffer: null` when there is nothing true and
 * positive to say (a legacy caregiver, a missed deadline), and an older API
 * sends no field at all; either way nothing renders.
 */
import { MaterialCommunityIcons } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";
import { StyleSheet, View } from "react-native";
import { Text } from "react-native-paper";

import { formatDateKey } from "@/components/bonuses/BonusesCard";
import type { OnboardingBonusOffer } from "@/lib/onboarding-api";
import { colors, fonts, gradients, radius, spacing } from "@/lib/theme";

function dollars(cents: number): string {
    return `$${(cents / 100).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
}

export function OnboardingBonusBanner({
    offer
}: {
    offer: OnboardingBonusOffer | null | undefined;
}) {
    if (!offer) {
        return null;
    }
    const amount = dollars(offer.amountCents);
    const daysLeft =
        offer.daysLeft <= 0
            ? "Last day"
            : offer.daysLeft === 1
              ? "1 day left"
              : `${offer.daysLeft} days left`;

    const content =
        offer.state === "open"
            ? {
                  icon: "gift-outline" as const,
                  title: `Finish by ${formatDateKey(offer.deadline)}, get ${amount}`,
                  body: `Complete your onboarding by ${formatDateKey(offer.deadline)} and earn a ${amount} bonus. It's added once your account is activated.`,
                  chip: daysLeft
              }
            : offer.state === "done_in_time"
              ? {
                    icon: "party-popper" as const,
                    title: `You made it in time for the ${amount} bonus`,
                    body: `It's added to your payments as soon as your account is activated.`,
                    chip: null
                }
              : {
                    icon: "check-decagram" as const,
                    title: `${amount} bonus earned`,
                    body: "You'll find it in your Payments tab.",
                    chip: null
                };

    return (
        <View style={styles.card}>
            <LinearGradient
                colors={[...gradients.brand]}
                start={{ x: 0, y: 0 }}
                end={{ x: 1, y: 1 }}
                style={styles.iconCircle}
            >
                <MaterialCommunityIcons name={content.icon} size={22} color={colors.surface} />
            </LinearGradient>
            <View style={styles.textWrap}>
                <View style={styles.titleRow}>
                    <Text style={styles.title}>{content.title}</Text>
                </View>
                <Text style={styles.body}>{content.body}</Text>
                {content.chip ? (
                    <View style={styles.chip}>
                        <MaterialCommunityIcons
                            name="clock-outline"
                            size={12}
                            color={colors.teal600}
                        />
                        <Text style={styles.chipText}>{content.chip}</Text>
                    </View>
                ) : null}
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    card: {
        flexDirection: "row",
        alignItems: "flex-start",
        gap: spacing.md,
        backgroundColor: colors.surface,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.teal200,
        padding: spacing.base,
        marginBottom: spacing.lg
    },
    iconCircle: {
        width: 40,
        height: 40,
        borderRadius: 20,
        alignItems: "center",
        justifyContent: "center"
    },
    textWrap: { flex: 1 },
    titleRow: { flexDirection: "row", alignItems: "center" },
    title: { fontSize: 16, fontFamily: fonts.bold, color: colors.textPrimary, marginBottom: 4 },
    body: { fontSize: 13, fontFamily: fonts.regular, color: colors.textSecondary, lineHeight: 19 },
    chip: {
        flexDirection: "row",
        alignItems: "center",
        alignSelf: "flex-start",
        gap: 4,
        marginTop: spacing.sm,
        backgroundColor: colors.teal50,
        borderRadius: radius.full,
        paddingHorizontal: 8,
        paddingVertical: 3
    },
    chipText: { fontSize: 11, fontFamily: fonts.semiBold, color: colors.teal600 }
});
